import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * The options for a referral's Specialty — the SERVICE NAMES of the four
 * consultation subtypes a referral can be made to (product, 2026-09-10):
 * Doctor, Nutritionists, Physiotherapist, Psychotherapist.
 *
 * FILTERED SERVER-SIDE, BY SUBTYPE ID (Nittin, 2026-09-10: the id path is the
 * reliable one long-term). Three calls, all endpoints that already exist and
 * that bridge's own Service dashboard is built on. Verified against
 * api-services-service on `release`:
 *
 *   1. GET  /v1/service/type
 *        -> a BARE ARRAY of service types (controllers/service_type.go:111).
 *           Each carries `serviceSubTypeReq` (models/service_type.go:16),
 *           which says whether asking for its subtypes is meaningful at all.
 *   2. GET  /v1/service/subtype/type/{typeId}
 *        -> a BARE ARRAY of { id, name, serviceTypeId, serviceTypeName }
 *           (controllers/service_subtype.go:279). The four are picked out of
 *           this by NAME; only their IDS go on the wire afterwards.
 *   3. POST /v1/service/service/filter
 *        with `serviceSubtypeIds: [...]`, which the query builder turns into
 *        `serviceSubtypeId: {$in: ids}` (repository/service.go:352-354).
 *
 * WHY NOT THE ONE-CALL VERSION. The filter's response also carries
 * `serviceSubtypeName`, so the four could be matched client-side in a single
 * request — this file did that first. The id path narrows where the data is,
 * keeps working when a subtype is renamed as long as its id is stable, and
 * stops the client depending on a display string on every row. The names now
 * identify the four only at lookup time (step 2).
 *
 * NOT RESTRICTED TO ACTIVE (Nittin, 2026-09-10 — asked for, then explicitly
 * reversed). All three calls deliberately pass NO status filter, so a
 * deactivated service or subtype is still offered as a specialty. That is a
 * choice, not an oversight, and the mechanics are worth knowing before anyone
 * "fixes" it:
 *
 *   · the two GETs read `status` as the string "true"/"false", compared with
 *     EqualFold (controllers/service_type.go:96, service_subtype.go:238);
 *   · the filter reads it as "active"/"inactive" (repository/service.go:324);
 *   · in BOTH, anything else — including the empty string these calls send —
 *     leaves the isActive constraint unset, which is exactly what is wanted
 *     here. The two conventions differ, so a future change has to set the
 *     right one in the right place.
 *
 * FETCHED ONCE PER SESSION. The chain is paid once and the search is local; the
 * whole service list is small (175 on the dashboard).
 */

/** The four, as product named them. Matched loosely — see `nameMatches`. */
export const REFERRAL_SUBTYPES = ["Doctor", "Nutritionists", "Physiotherapist", "Psychotherapist"];

/**
 * Subtype-name comparison, for step 2 only.
 *
 * Case-insensitive and tolerant of a trailing "s", because the catalogue is
 * inconsistent about the plural — the dashboard lists "Nutritionists" beside
 * "Physiotherapist" — and a referral list that silently lost every
 * nutritionist over an "s" would look like missing data rather than a matching
 * bug. "Medical team" is a fifth subtype and is deliberately not among them.
 */
const norm = (s) =>
  String(s == null ? "" : s)
    .trim()
    .toLowerCase()
    .replace(/s$/, "");
const WANTED = REFERRAL_SUBTYPES.map(norm);
const nameMatches = (name) => WANTED.indexOf(norm(name)) !== -1;

/* Module-level, like the procedure catalogue's cache: a picker opened a second
   time renders instantly, and two referrals added in one sitting share one
   chain. Holds the resolved array, or the promise while it is in flight, so a
   double mount cannot fire the requests twice. */
let cache = null;
let inFlight = null;

/** One APIService for the module. */
let api = null;
const apiService = () => {
  if (!api) api = new APIService();
  return api;
};

/* Substituted here rather than through String.prototype.format: that extension
   is installed by Helper's _initializeNaturalExtensions(), which this module
   cannot assume has run. */
const fill = (tpl, ...args) =>
  args.reduce((s, v, i) => s.replace(`{${i}}`, String(v)), tpl);

/* Promise wrappers over the callback-style client, so the three steps read as
   the sequence they are. Each RESOLVES on failure rather than rejecting — see
   the note on loadReferralSpecialties. */
const get = (url) =>
  new Promise((resolve) => {
    apiService().get(url, (res) => resolve(res), () => resolve(null));
  });
const post = (url, body) =>
  new Promise((resolve) => {
    apiService().post(url, body, (res) => resolve(res), () => resolve(null));
  });

/** Both GETs answer with a bare array; be liberal about a wrapper anyway. */
const asRows = (res) => {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
};

/** The ids of the four subtypes, looked up by name across every service type. */
const subtypeIds = async () => {
  /* "" for status — unrestricted, see the note above. */
  const types = asRows(await get(fill(APIConfig.myPlan.createService.getServiceType, "")));
  /* Only types that HAVE subtypes are asked. Two reasons: the answer is
     meaningless otherwise, and the endpoint 404s rather than answering `[]`
     when a type has none (controllers/service_subtype.go:252-255), so asking
     anyway would spend a failed request per subtype-less type. */
  const withSubtypes = types.filter((t) => t && t.serviceSubTypeReq && t.id != null);

  const lists = await Promise.all(
    withSubtypes.map((t) =>
      get(fill(APIConfig.myPlan.createService.getServiceSubType, t.id, ""))
    )
  );

  const ids = [];
  const seen = {};
  lists.forEach((res) => {
    asRows(res).forEach((st) => {
      if (!st || st.id == null || !nameMatches(st.name)) return;
      /* A subtype name can legitimately exist under more than one type; every
         matching id counts, and each is added once. */
      if (seen[st.id]) return;
      seen[st.id] = true;
      ids.push(st.id);
    });
  });
  return ids;
};

/**
 * The specialty options, resolved once.
 *
 * Resolves to `[]` rather than rejecting at every step: the field falls back to
 * accepting a typed specialty, and a referral that cannot be written because a
 * catalogue read failed would be a worse outcome than one typed by hand.
 */
export const loadReferralSpecialties = () => {
  if (cache) return Promise.resolve(cache);
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const ids = await subtypeIds();
    /* No ids means the lookup failed, or the catalogue holds none of the four.
       RETURNING HERE IS LOAD-BEARING: an empty `serviceSubtypeIds` applies no
       subtype constraint at all (`if len(filters.ServiceSubtypeIds) > 0`,
       repository/service.go:352), so posting the filter without them would
       quietly widen the picker to EVERY service Zyla sells. */
    if (!ids.length) {
      inFlight = null;
      return [];
    }

    const res = await post(APIConfig.myPlan.serviceDashboard.fetchServicesByFilter, {
      /* `pageSize: 0` really is unpaginated — the $skip/$limit stage is added
         only `if filters.PageNum > 0 && filters.PageSize > 0`
         (repository/service.go:483). `nameMatch`/`serviceName` mirror what the
         appointments dashboard's own service loader sends. No `status`: see
         the note at the top. */
      pageNum: 0,
      pageSize: 0,
      nameMatch: [],
      serviceName: "",
      serviceSubtypeIds: ids,
    });

    const rows = (res && res.services) || [];
    const seen = {};
    const out = [];
    rows.forEach((r) => {
      if (!r) return;
      /* `serviceName` is the human name; `name` is the CODE ("ML12"), which is
         the mix-up the appointments loader already had to fix once. A row with
         no name is dropped rather than listed as its code. */
      const label = String((r.serviceName || "").trim());
      if (!label) return;
      /* The catalogue really does repeat a service name across rows (the
         dashboard dedupes the same way, filterUniqueByServiceName), and a
         referral cares about the name, not which row it came from. */
      const key = label.toLowerCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push({ label, subtype: String(r.serviceSubtypeName || "").trim() });
    });
    out.sort((a, b) => a.label.localeCompare(b.label));

    /* Only a real answer is cached. A failed chain should be retried the next
       time the picker opens, not remembered as "there are no specialties". */
    inFlight = null;
    if (out.length) cache = out;
    return out;
  })();

  return inFlight;
};

/** Test seam — lets a harness re-run the chain without a reload. */
export const __resetReferralSpecialtyCache = () => {
  cache = null;
  inFlight = null;
};

export default loadReferralSpecialties;
